import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { IoPeopleOutline, IoPersonOutline, IoTrashOutline, IoMailOutline, IoCloseOutline } from "react-icons/io5";
import NavBar from "../../components/common/NavBar";
import Spinner from "../../components/common/Spinner";
import ChangePasswordDialog from "../../components/auth/ChangePasswordDialog";
import { useAuth } from "../../contexts/AuthContext";
import { useOrg } from "../../hooks/useOrg";
import { orgApi, orgErrorMessage, OrgApiError, OrgMember, OrgRole, INVITE_TTL_DAYS } from "../../services/orgApi";

// Settings (Trello #562): "Your account" for everyone, "Team & users" for an
// organisation's editors — the list with roles and Active/Invited status, the
// plan's user limit, invite, change role, resend, cancel and remove. All rules
// (limit, last editor, editors only) are enforced by the backend; the page
// explains its refusals and mirrors the obvious ones so buttons aren't offered
// that would only be refused.

const PeopleIcon = IoPeopleOutline as any;
const PersonIcon = IoPersonOutline as any;
const TrashIcon = IoTrashOutline as any;
const MailIcon = IoMailOutline as any;
const CloseIcon = IoCloseOutline as any;

const SUPPORT_EMAIL = "support@telcofy.ai";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const roleLabel = (r: OrgRole) => (r === "editor" ? "Editor" : "Viewer");
const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null;

/** Shown when the plan's user limit is reached: what it means and how to get more. */
const LimitDialog: React.FC<{ included: number; onClose: () => void }> = ({ included, onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-scrim" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-labelledby="limit-title" className="relative w-full max-w-sm rounded-2xl bg-surface p-6 shadow-2xl">
        <h3 id="limit-title" className="text-lg font-semibold text-ink">
          Your plan's users are all in use
        </h3>
        <p className="mt-2 text-sm text-ink-muted">
          Your plan includes {included} users, and pending invites count too. To add someone, remove a user or cancel an invite,
          or contact Telcofy to add more users to your plan.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-ink-muted hover:bg-paper-100">
            Close
          </button>
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("More users on our Telcofy plan")}`}
            className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-on-brand hover:bg-brand-600"
          >
            Email {SUPPORT_EMAIL}
          </a>
        </div>
      </div>
    </div>
  );
};

const TeamTab: React.FC<{ onChanged: () => void }> = ({ onChanged }) => {
  const [members, setMembers] = useState<OrgMember[] | null>(null);
  const [included, setIncluded] = useState(0);
  const [inUse, setInUse] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // uid (or "invite") of the action in flight
  const [message, setMessage] = useState<{ kind: "error" | "ok"; text: string } | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState<OrgRole>("viewer");
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [showLimit, setShowLimit] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await orgApi.listMembers();
      setMembers(r.members);
      setIncluded(r.usersIncluded);
      setInUse(r.usersInUse);
      setLoadError(null);
    } catch (err) {
      setLoadError(orgErrorMessage(err));
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const after = async (text: string) => {
    setMessage({ kind: "ok", text });
    await load();
    onChanged();
  };
  const fail = (err: unknown) => {
    if (err instanceof OrgApiError && err.code === "seat_limit_reached") setShowLimit(true);
    setMessage({ kind: "error", text: orgErrorMessage(err) });
  };
  // `ok` can depend on what the backend answered (e.g. whether an invite email went out).
  const run = async <T,>(key: string, fn: () => Promise<T>, ok: string | ((r: T) => string)) => {
    setBusy(key);
    setMessage(null);
    try {
      const r = await fn();
      await after(typeof ok === "function" ? ok(r) : ok);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };

  if (loadError)
    return (
      <div className="rounded-2xl border border-bad-line bg-bad-soft p-6 text-sm text-bad-ink">
        Couldn't load your team: {loadError}
      </div>
    );
  if (!members)
    return (
      <div className="py-16">
        <Spinner />
      </div>
    );

  const full = included > 0 && inUse >= included;
  const editors = members.filter((m) => m.role === "editor").length;
  const isLastEditor = (m: OrgMember) => m.role === "editor" && editors <= 1;

  const invite = (e: React.FormEvent) => {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) return setMessage({ kind: "error", text: "Enter a valid email address." });
    if (full) return setShowLimit(true);
    run(
      "invite",
      async () => {
        const r = await orgApi.invite(email, newRole);
        setNewEmail("");
        return r;
      },
      // Someone new gets an invite to set a password; someone who has signed in
      // before is added (back) straight away and emailed that they have access.
      (r) =>
        r.emailKind === "invite" || (r.emailSent && !r.emailKind)
          ? `Invite sent to ${email}. They'll set their own password from the email.`
          : `${email} ${r.emailKind === "access_restored" ? "is back in" : "has been added to"} your team and can sign in with their existing password.${
              r.emailSent ? " We've emailed them." : ""
            }`
    );
  };

  return (
    <section className="rounded-2xl border border-paper-300/70 bg-surface p-6 shadow-soft">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <h1 className="font-display text-xl font-bold text-ink">Team &amp; users</h1>
        <div className="w-48">
          <p className="text-right text-sm text-ink">
            <span className="font-semibold">{inUse}</span> of {included} users
          </p>
          <div className="mt-1.5 h-2 rounded-full bg-paper-200">
            <div
              className={`h-2 rounded-full ${full ? "bg-warn" : "bg-brand-500"}`}
              style={{ width: `${included ? Math.min(100, (inUse / included) * 100) : 0}%` }}
            />
          </div>
        </div>
      </div>

      <form onSubmit={invite} className="mt-6 flex flex-wrap items-end gap-2 rounded-xl bg-paper-100 p-3">
        <label className="flex-1 text-xs text-ink-muted">
          Invite by email
          <input
            type="email"
            value={newEmail}
            onChange={(e) => {
              setNewEmail(e.target.value);
              setMessage(null);
            }}
            disabled={busy === "invite"}
            placeholder="name@example.no"
            className="mt-1 block w-full min-w-[14rem] rounded-lg border border-paper-300 bg-surface px-3 py-1.5 text-sm text-ink"
          />
        </label>
        <label className="text-xs text-ink-muted">
          Role
          <select
            value={newRole}
            onChange={(e) => setNewRole(e.target.value as OrgRole)}
            className="mt-1 block rounded-lg border border-paper-300 bg-surface px-2 py-1.5 text-sm text-ink"
          >
            <option value="viewer">Viewer</option>
            <option value="editor">Editor</option>
          </select>
        </label>
        <button
          type="submit"
          disabled={!newEmail.trim() || busy === "invite"}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-semibold text-on-brand transition-colors hover:bg-brand-600 disabled:opacity-50"
        >
          <MailIcon className="h-4 w-4" /> {busy === "invite" ? "Sending…" : "Send invite"}
        </button>
      </form>

      {message && <p className={`mt-3 text-sm ${message.kind === "error" ? "text-bad-ink" : "text-brand-700"}`}>{message.text}</p>}

      <table className="mt-5 w-full text-sm">
        <thead>
          <tr className="border-b border-paper-200 text-left text-ink-muted">
            <th className="py-2 pr-3 font-medium">User</th>
            <th className="py-2 pr-3 font-medium">Role</th>
            <th className="py-2 pr-3 font-medium">Status</th>
            <th className="py-2" />
          </tr>
        </thead>
        <tbody>
          {members.map((m) => {
            const locked = isLastEditor(m);
            const working = busy === m.uid;
            const invited = m.status === "invited";
            const note = invited
              ? `Invited ${fmtDate(m.invitedAt) ?? ""}`.trim()
              : m.lastActiveAt
                ? `Last active ${fmtDate(m.lastActiveAt)}`
                : m.lastSignInAt
                  ? `Last signed in ${fmtDate(m.lastSignInAt)}`
                  : null;
            return (
              <tr key={m.uid} className="border-b border-paper-200 align-middle last:border-0">
                <td className="py-3 pr-3">
                  <span className="text-ink">{m.displayName || m.email}</span>
                  {m.isYou && <span className="ml-2 rounded-full bg-paper-200 px-1.5 py-0.5 text-[10px] text-ink-subtle">You</span>}
                  {m.displayName && <p className="text-xs text-ink-muted">{m.email}</p>}
                  {note && <p className="text-xs text-ink-faint">{note}</p>}
                </td>
                <td className="py-3 pr-3">
                  <select
                    value={m.role}
                    disabled={locked || working}
                    title={locked ? "Your organisation needs at least one editor." : undefined}
                    onChange={(e) => {
                      const role = e.target.value as OrgRole;
                      run(m.uid, () => orgApi.changeRole(m.uid, role), `${m.email} is now ${roleLabel(role).toLowerCase()}.`);
                    }}
                    className="rounded-lg border border-paper-300 bg-surface px-2 py-1 text-sm text-ink disabled:opacity-60"
                  >
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </td>
                <td className="py-3 pr-3">
                  {invited ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="rounded-full bg-paper-200 px-2 py-0.5 text-xs font-medium text-ink-muted">Invited</span>
                      <button
                        onClick={() => run(m.uid, () => orgApi.resendInvite(m.uid), `Invite sent to ${m.email} again.`)}
                        disabled={working}
                        className="text-xs font-medium text-brand-600 hover:underline disabled:opacity-50"
                      >
                        Resend
                      </button>
                    </span>
                  ) : (
                    <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">Active</span>
                  )}
                </td>
                <td className="py-3 text-right">
                  {confirmRemove === m.uid ? (
                    <span className="inline-flex items-center gap-2 text-xs">
                      <span className="text-ink-muted">{invited ? "Cancel invite?" : "Remove?"}</span>
                      <button
                        onClick={() => {
                          setConfirmRemove(null);
                          run(
                            m.uid,
                            () => orgApi.remove(m.uid),
                            (r) =>
                              invited
                                ? `Invite to ${m.email} cancelled.`
                                : `${m.email} has been removed and can no longer sign in.${r?.emailSent ? " We've emailed them." : ""}`
                          );
                        }}
                        className="font-semibold text-bad-ink hover:underline"
                      >
                        {invited ? "Yes, cancel" : "Yes, remove"}
                      </button>
                      <button onClick={() => setConfirmRemove(null)} className="text-ink-muted hover:underline">
                        Keep
                      </button>
                    </span>
                  ) : (
                    <button
                      onClick={() => setConfirmRemove(m.uid)}
                      disabled={locked || working}
                      title={locked ? "Your organisation needs at least one editor." : invited ? `Cancel the invite to ${m.email}` : `Remove ${m.email}`}
                      aria-label={invited ? `Cancel the invite to ${m.email}` : `Remove ${m.email}`}
                      className="rounded-lg p-1.5 text-ink-faint transition-colors hover:bg-paper-100 hover:text-bad-ink disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-ink-faint"
                    >
                      {invited ? <CloseIcon className="h-4 w-4" /> : <TrashIcon className="h-4 w-4" />}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="mt-5 space-y-1 text-xs leading-relaxed text-ink-faint">
        <p>
          <b className="text-ink-muted">Editors</b> manage users and areas and switch monitoring on or off.{" "}
          <b className="text-ink-muted">Viewers</b> use every dashboard, but can't change areas, monitoring, saved totals or users.
          Use Viewer for partners such as the police.
        </p>
        <p>
          An invite holds a place until it's accepted or cancelled, or expires after {INVITE_TTL_DAYS} days. Removing a user signs
          them out and stops their access; nothing they made is deleted, and inviting them again restores it.
        </p>
      </div>

      {showLimit && <LimitDialog included={included} onClose={() => setShowLimit(false)} />}
    </section>
  );
};

const SettingsPage: React.FC = () => {
  const { tab = "account" } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { info, organisation, role, isEditor, error, refresh } = useOrg();
  const [changingPassword, setChangingPassword] = useState(false);

  const tabClass = (t: string) =>
    `flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
      tab === t ? "bg-brand-50 font-medium text-brand-700" : "text-ink-muted hover:bg-paper-100 hover:text-ink"
    }`;

  return (
    <div className="min-h-screen">
      <NavBar title="Settings" showBackButton />
      <div className="mx-auto max-w-5xl px-4 py-8">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-[12rem_1fr]">
          <nav className="space-y-1">
            <button className={tabClass("account")} onClick={() => navigate("/settings/account")}>
              <PersonIcon className="h-4 w-4" /> Your account
            </button>
            {isEditor && (
              <button className={tabClass("team")} onClick={() => navigate("/settings/team")}>
                <PeopleIcon className="h-4 w-4" /> Team &amp; users
              </button>
            )}
          </nav>

          {tab === "team" ? (
            !info && !error ? (
              <div className="py-16">
                <Spinner />
              </div>
            ) : isEditor ? (
              <TeamTab onChanged={refresh} />
            ) : (
              <section className="rounded-2xl border border-paper-300/70 bg-surface p-6 text-sm text-ink-muted shadow-soft">
                {organisation
                  ? "Only your organisation's editors can manage the team."
                  : error
                    ? "Couldn't load your organisation. Please try again."
                    : "Your account isn't part of an organisation, so there's no team to manage."}{" "}
                <Link to="/settings/account" className="font-medium text-brand-600 hover:underline">
                  Your account
                </Link>
              </section>
            )
          ) : (
            <section className="rounded-2xl border border-paper-300/70 bg-surface p-6 shadow-soft">
              <h1 className="font-display text-xl font-bold text-ink">Your account</h1>
              <dl className="mt-4 grid grid-cols-[8rem_1fr] gap-y-2 text-sm">
                <dt className="text-ink-muted">Email</dt>
                <dd className="text-ink">{currentUser?.email}</dd>
                {organisation && (
                  <>
                    <dt className="text-ink-muted">Organisation</dt>
                    <dd className="text-ink">
                      {organisation.name ?? "—"}
                      {organisation.planName && <span className="text-ink-muted"> · {organisation.planName}</span>}
                    </dd>
                    <dt className="text-ink-muted">Role</dt>
                    <dd className="text-ink">{role ? roleLabel(role) : "—"}</dd>
                  </>
                )}
              </dl>
              <button
                onClick={() => setChangingPassword(true)}
                className="mt-5 rounded-lg border border-paper-300 px-3 py-1.5 text-sm font-medium text-ink transition-colors hover:bg-paper-100"
              >
                Change password
              </button>
            </section>
          )}
        </div>
      </div>
      <ChangePasswordDialog open={changingPassword} onClose={() => setChangingPassword(false)} />
    </div>
  );
};

export default SettingsPage;
